import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Plus,
  Search,
  ArrowUpDown,
  BarChart2,
  MapPin,
  Mail,
  Phone,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useActiveClinic } from '@/lib/activeClinic';
import { useClinic } from '@/hooks/useClinicSettings';
import { useSubClinics, useActivateMultiClinic } from '@/hooks/useMultiClinic';
import { leavesSubClinicSettings, subClinicSettingsAccess } from '@/lib/subClinicSettingsAccess';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { StatusChip } from '@/components/ui/Chip';
import { TYPE } from '@/lib/typography';
import {
  SectionCard,
  SettingsEmpty,
  SettingsLoading,
  SettingsStack,
} from './settingsChrome';
import { CreateSubClinicModal } from '@/components/modals/CreateSubClinicModal';
import { toast } from 'react-hot-toast';
import { cn } from '@/utils/cn';
import type { Json } from '@/types/database.generated';

type SortField = 'name' | 'created_at' | 'city';

interface AddressShape {
  street?: string;
  city?: string;
  zip?: string;
  country?: string;
}

function readAddress(value: Json): AddressShape {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const v = value as Record<string, Json>;
    return {
      street: typeof v.street === 'string' ? v.street : '',
      city: typeof v.city === 'string' ? v.city : '',
      zip: typeof v.zip === 'string' ? v.zip : '',
      country: typeof v.country === 'string' ? v.country : 'Danmark',
    };
  }
  return {};
}

function readActivatedModules(value: Json): Record<string, boolean> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, boolean>;
  }
  return {};
}

export function ClinicsClinics() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { activeClinicId, activeClinic } = useActiveClinic();
  const { data: clinic, isLoading: clinicLoading } = useClinic(activeClinicId);
  const { data: subClinics = [], isLoading: subClinicsLoading } = useSubClinics(activeClinicId);
  const activateModule = useActivateMultiClinic(activeClinicId);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showStats, setShowStats] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Check if multi-clinic is activated
  const activatedModules = readActivatedModules(clinic?.activated_modules ?? null);
  const hasMultiClinicAccess = Boolean(activatedModules.multiClinic) || subClinics.length > 0;

  // Leave for Generelt only when this clinic cannot show Underklinikker
  // (sub-clinic, or no multi_clinic view). clinic_admin and custom roles
  // that can see the menu item stay on the page. Wait until the active
  // clinic membership is resolved so a cached clinic row cannot bounce
  // the user before the role is known.
  const access = subClinicSettingsAccess({
    activeClinicId,
    activeClinicResolved: activeClinic != null,
    isSubClinic: activeClinic?.isSubClinic === true,
    canView: Boolean(activeClinicId && hasPermission(activeClinicId, 'multi_clinic', 'view')),
  });

  const leave = leavesSubClinicSettings(access);

  useEffect(() => {
    if (leave) navigate('/settings/general', { replace: true });
  }, [leave, navigate]);

  const handleActivateMultiClinic = async () => {
    try {
      await activateModule.mutateAsync();
      toast.success(t('settings.clinics.multi_clinic_activated'));
    } catch (error) {
      console.error('Failed to activate multi-clinic:', error);
      toast.error(t('settings.clinics.activation_error'));
    }
  };

  // Filter and sort clinics
  const filteredAndSortedClinics = useMemo(() => {
    let filtered = [...subClinics];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((clinic) => {
        const address = readAddress(clinic.address);
        return (
          clinic.name.toLowerCase().includes(term) ||
          clinic.email.toLowerCase().includes(term) ||
          address.city?.toLowerCase().includes(term)
        );
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'city': {
          const aAddr = readAddress(a.address);
          const bAddr = readAddress(b.address);
          comparison = (aAddr.city ?? '').localeCompare(bAddr.city ?? '');
          break;
        }
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [subClinics, searchTerm, sortField, sortOrder]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalClinics = subClinics.length;
    const activeClinics = subClinics.filter((c) => c.is_active).length;
    const cities = new Set(
      subClinics.map((c) => readAddress(c.address).city).filter(Boolean)
    );

    return {
      totalClinics,
      activeClinics,
      citiesCount: cities.size,
    };
  }, [subClinics]);

  if (leave) return null;

  if (clinicLoading || subClinicsLoading) {
    return <SettingsLoading />;
  }

  return (
    <div className="flex min-w-0 flex-col gap-s4">
      {!hasMultiClinicAccess ? (
        <Card>
          <div className="flex items-start gap-s4">
            <div className="rounded-ctl bg-ds-teal-tint p-s3">
              <Building2 className="h-6 w-6 text-ds-teal" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={cn(TYPE.heading, 'text-ds-text')}>
                {t('settings.clinics.activate_title')}
              </h3>
              <p className={cn(TYPE.body, 'mt-s2 text-ds-muted')}>
                {t('settings.clinics.activate_description')}
              </p>
              <Button
                onClick={handleActivateMultiClinic}
                loading={activateModule.isPending}
                loadingText={t('settings.clinics.activating')}
                className="mt-s4 w-full nav:w-auto"
              >
                {t('settings.clinics.activate_button')}
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      <SectionCard
        title={t('settings.clinics.title')}
        subtitle={t('settings.clinics.subtitle')}
        action={
          hasMultiClinicAccess ? (
            <div className="flex w-full flex-col gap-s2 nav:w-auto nav:flex-row">
              <Button
                variant="secondary"
                onClick={() => setShowStats(!showStats)}
                className="w-full nav:w-auto"
              >
                <BarChart2 className="h-4 w-4" />
                {showStats ? t('settings.clinics.hide_stats') : t('settings.clinics.show_stats')}
              </Button>
              <Button onClick={() => setShowCreateModal(true)} className="w-full nav:w-auto">
                <Plus className="h-4 w-4" />
                {t('settings.clinics.create_clinic')}
              </Button>
            </div>
          ) : undefined
        }
      >
        {showStats ? (
          <div className="mb-s4 grid grid-cols-1 gap-s3 nav:grid-cols-3">
            <Card>
              <h4 className={cn(TYPE.small, 'text-ds-muted')}>
                {t('settings.clinics.stats.total_clinics')}
              </h4>
              <p className={cn(TYPE.display, 'mt-s2 text-ds-text')}>{stats.totalClinics}</p>
            </Card>
            <Card>
              <h4 className={cn(TYPE.small, 'text-ds-muted')}>
                {t('settings.clinics.stats.active_clinics')}
              </h4>
              <p className={cn(TYPE.display, 'mt-s2 text-ds-text')}>{stats.activeClinics}</p>
            </Card>
            <Card>
              <h4 className={cn(TYPE.small, 'text-ds-muted')}>{t('settings.clinics.stats.cities')}</h4>
              <p className={cn(TYPE.display, 'mt-s2 text-ds-text')}>{stats.citiesCount}</p>
            </Card>
          </div>
        ) : null}

        <div className="mb-s4 flex min-w-0 flex-col gap-s3 nav:flex-row">
          <div className="min-w-0 flex-1">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('settings.clinics.search_placeholder')}
              leadingIcon={<Search className="h-4 w-4" aria-hidden />}
            />
          </div>
          <div className="flex items-end gap-s2">
            <Select
              value={sortField}
              onChange={(e) => setSortField(e.target.value as SortField)}
              className="min-w-0 flex-1"
            >
              <option value="name">{t('settings.clinics.sort.name')}</option>
              <option value="created_at">{t('settings.clinics.sort.created_at')}</option>
              <option value="city">{t('settings.clinics.sort.city')}</option>
            </Select>
            <Button
              type="button"
              variant="secondary"
              size="icon"
              onClick={() => setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'))}
              aria-label={t('settings.clinics.toggle_sort_order')}
            >
              <ArrowUpDown className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {filteredAndSortedClinics.length === 0 ? (
          <SettingsEmpty
            icon={<Building2 className="h-7 w-7" />}
            title={
              searchTerm ? t('settings.clinics.no_results') : t('settings.clinics.no_clinics')
            }
          />
        ) : (
          <SettingsStack>
            {filteredAndSortedClinics.map((clinic) => {
              const address = readAddress(clinic.address);
              return (
                <div
                  key={clinic.id}
                  className={cn(
                    'flex min-w-0 flex-col gap-s3 rounded-ctl border border-ds-line p-s4 nav:flex-row nav:items-start',
                    !clinic.is_active && 'opacity-60',
                  )}
                >
                  <div className="rounded-ctl bg-ds-teal-tint p-s3">
                    <Building2 className="h-6 w-6 text-ds-teal" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-s2">
                      <h3 className={cn(TYPE.heading, 'text-ds-text')}>{clinic.name}</h3>
                      {!clinic.is_active ? (
                        <StatusChip tone="neutral">{t('settings.clinics.inactive')}</StatusChip>
                      ) : null}
                    </div>
                    <div className={cn(TYPE.small, 'mt-s2 space-y-s2 text-ds-muted')}>
                      {address.street ? (
                        <div className="flex items-center gap-s2">
                          <MapPin className="h-4 w-4 shrink-0" aria-hidden />
                          {address.street}, {address.zip} {address.city}
                        </div>
                      ) : null}
                      <div className="flex items-center gap-s2">
                        <Mail className="h-4 w-4 shrink-0" aria-hidden />
                        <span className="min-w-0 break-all">{clinic.email}</span>
                      </div>
                      {clinic.phone ? (
                        <div className="flex items-center gap-s2">
                          <Phone className="h-4 w-4 shrink-0" aria-hidden />
                          {clinic.phone}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    className="w-full nav:w-auto"
                    onClick={() => navigate(`/settings/clinics/${clinic.id}`)}
                  >
                    {t('settings.clinics.manage')}
                  </Button>
                </div>
              );
            })}
          </SettingsStack>
        )}
      </SectionCard>

      {/* Create sub-clinic modal */}
      <CreateSubClinicModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        parentClinicType={clinic?.type}
        onSuccess={(clinicId) => {
          setShowCreateModal(false);
          toast.success(t('settings.clinics.created_successfully'));
          navigate(`/settings/clinics/${clinicId}`);
        }}
      />
    </div>
  );
}
